/**
 * FlashBook — persistence helpers.
 *
 * Publishing and draft-saving share the same media pipeline: every local
 * (`file:` / `blob:` / `data:` / relative) URI — the cover artwork, the cover
 * video, the cover intro audio and each block's reading audio — is uploaded as
 * a blob and swapped for its public URL. Records therefore always hold remote
 * URLs, so a book renders for everyone, not just for the device that wrote it.
 */
import {type AtpAgent, type BlobRef} from '@atproto/api'
import {type I18n} from '@lingui/core'

import {uploadBlob} from '#/lib/api'
import {BSKY_SERVICE} from '#/lib/constants'
import {isLocalClipUrl} from '#/lib/flashbookClips'
import {filmCoverStill} from '#/lib/flashbookCoverStill'
import {flashbookPublishTarget} from '#/lib/flashbookPublish'
import {processVideo, type VideoAction} from '#/view/com/composer/state/video'
import {type Slide} from '#/types/capsule'
import {type FlashBookCover, type FlashBookPage} from '#/types/flashbook'

/**
 * A URI that only exists on this device and still needs uploading.
 *
 * Delegates to the clip vocabulary (`#/lib/flashbookClips`) so that one rule —
 * not two that look alike — decides what the save pipeline still has to send.
 */
export function isLocalUri(u?: string) {
  return isLocalClipUrl(u)
}

/**
 * The public URL a blob is served from.
 *
 * An upload answers with a *reference* — a hash of the bytes — and a reference is
 * not something a surface can draw: what an `Image` needs is the address the world
 * fetches them from. That is this, sitting beside the upload that produced the
 * reference, because the two are one question asked twice: *where did these bytes
 * go, and how does somebody else see them*. Nothing here is book-shaped — it is
 * `getBlob` on the service the upload went to — and it is deliberately not a shared
 * helper either: a blob served by address is a blob a *record* references, which is
 * exactly what a FlashBook has and a device-local classroom cover does not.
 */
export function resolveBlobUrl(did: string, blobRef: BlobRef) {
  return `${BSKY_SERVICE}/xrpc/com.atproto.sync.getBlob?did=${encodeURIComponent(
    did,
  )}&cid=${encodeURIComponent(blobRef.ref.toString())}`
}

/**
 * Uploads one clip and returns the URL the world can fetch it from.
 *
 * Shared by the two moments a clip can be made durable: *on capture* — a take
 * recorded, uploaded or edited in a block, which the field sends immediately so
 * a reload, a crash or another device never loses it — and the save-time sweep
 * below, which is left to cover whatever a signed-out creator recorded locally.
 * Both produce the same URL for the same bytes, so a save finds nothing local
 * left to send and a clip is never uploaded twice under two identities.
 */
export async function uploadClip({
  agent,
  did,
  uri,
  encoding,
}: {
  agent: AtpAgent
  did: string
  uri: string
  /**
   * The blob's type, when the platform cannot be trusted to name it.
   *
   * A recorded take arrives as a file with an extension; a decoded film still
   * arrives from a decoder as a temporary file whose name says nothing about what
   * it holds. Without this the PDS is handed an untyped blob, which is a blob some
   * readers will refuse to serve as a picture.
   */
  encoding?: string
}): Promise<string> {
  const res = await uploadBlob(agent, uri, encoding)
  return resolveBlobUrl(did, res.data.blob)
}

/**
 * The still a film cover is shown by, read from the film itself.
 *
 * The rule — which instant, what happens when it cannot be read, and how long a
 * save will wait for it — lives in `#/lib/flashbookCoverStill` with its own tests.
 * It is read here at save and publish rather than on every surface that needs it,
 * so the work happens once, where the creator is waiting anyway, and every reader
 * gets the picture from the record.
 */
export function deriveFilmCoverStill(
  cover: FlashBookCover,
): Promise<string | undefined> {
  return filmCoverStill(cover)
}

export type UploadedFlashBookMedia = {
  cover: FlashBookCover
  pages: FlashBookPage[]
  /** Present when the cover is a video — needed for the post embed. */
  videoEmbed?: Record<string, unknown>
}

/**
 * Uploads every local media URI a book references and returns the same book
 * with all URIs pointing at public blob URLs.
 */
export async function uploadFlashBookMedia({
  agent,
  i18n,
  did,
  cover,
  pages,
}: {
  agent: AtpAgent
  i18n: I18n
  did: string
  cover: FlashBookCover
  pages: FlashBookPage[]
}): Promise<UploadedFlashBookMedia> {
  const coverOut: FlashBookCover = {...cover}
  let videoEmbed: Record<string, unknown> | undefined

  if (cover.kind === 'video' && cover.videoUri && isLocalUri(cover.videoUri)) {
    const blobRef = await new Promise<BlobRef>((resolve, reject) => {
      void processVideo(
        {uri: cover.videoUri!, width: 1280, height: 720},
        (action: VideoAction) => {
          if (action.type === 'to_done') {
            resolve(action.blobRef)
          } else if (action.type === 'to_error') {
            reject(new Error(action.error))
          }
        },
        agent,
        did,
        new AbortController().signal,
        i18n,
      )
    })
    coverOut.videoUri = resolveBlobUrl(did, blobRef)
    videoEmbed = {
      $type: 'app.bsky.embed.video',
      video: blobRef,
      aspectRatio: {width: 16, height: 10},
    }
  }

  /*
   * The film's still, so every surface that shows the cover without playing it
   * has a picture rather than a rectangle. Read from the film as the studio handed
   * it over — the local file when there is one, since decoding our own upload back
   * off the network would be a great deal of traffic for a frame we have here.
   */
  if (coverOut.kind === 'video' && coverOut.videoUri) {
    const still = await deriveFilmCoverStill(cover)
    if (still) {
      coverOut.videoThumbnailUri = isLocalUri(still)
        ? await uploadClip({
            agent,
            did,
            uri: still,
            encoding: 'image/jpeg',
          })
        : still
    }
  }

  if (coverOut.imageUri && isLocalUri(coverOut.imageUri)) {
    coverOut.imageUri = await uploadClip({
      agent,
      did,
      uri: coverOut.imageUri,
    })
  }
  if (coverOut.audioUri && isLocalUri(coverOut.audioUri)) {
    coverOut.audioUri = await uploadClip({
      agent,
      did,
      uri: coverOut.audioUri,
    })
  }

  const pagesOut: FlashBookPage[] = []
  for (const page of pages) {
    let blocks: Slide[] = page.blocks
    if (blocks.some(b => b.readingAudioUrl && isLocalUri(b.readingAudioUrl))) {
      blocks = []
      for (const b of page.blocks) {
        if (b.readingAudioUrl && isLocalUri(b.readingAudioUrl)) {
          blocks.push({
            ...b,
            readingAudioUrl: await uploadClip({
              agent,
              did,
              uri: b.readingAudioUrl,
            }),
          })
        } else {
          blocks.push(b)
        }
      }
    }
    pagesOut.push({...page, blocks})
  }

  return {cover: coverOut, pages: pagesOut, videoEmbed}
}

/** Blocks that actually ask the learner something (used for counts). */
export function countExercises(pages: FlashBookPage[]) {
  return pages.reduce(
    (acc, p) =>
      acc +
      p.blocks.filter(b => b.type !== 'content' && b.type !== 'accordion')
        .length,
    0,
  )
}

export type SavedFlashBookRef = {
  uri: string
  cid: string
  /**
   * The record's own `createdAt`. The studio keeps this so that publishing a
   * book that came from a draft can convert the record in place without
   * backdating the book to the moment it was published.
   */
  createdAt: string
  /**
   * The cover as the *record* holds it, so it points at public blob URLs.
   *
   * Handed back because the studio's own copy still holds the picks it made — a
   * `blob:` URL on web, a `file:` path on a phone — and the drafts list stores a
   * cover per row to show the creator their own artwork. A row built from those
   * picks would paint a broken image the moment the tab was reloaded, while the
   * uploaded URL beside it is the one the record itself serves from forever.
   */
  cover: FlashBookCover
}

/**
 * Saves an unfinished book as a draft record. Drafts are private by design:
 * they create no post, so nothing shows up in feeds — the record only exists so
 * the creator can pick the work back up later. Publishing reuses this same
 * record (see `PublishFlashBookDialog`), so a draft is converted rather than
 * left behind as an orphan.
 *
 * Saving a draft that came from a draft *updates* that record rather than
 * creating another one. Without this, every "Save" click during one editing
 * session left a separate half-finished book behind, and the drafts list filled
 * up with near-identical copies of the same work — the creator's own list, made
 * useless by the tool that was supposed to protect their work.
 */
export async function saveFlashBookDraft({
  agent,
  i18n,
  did,
  title,
  description,
  themeId,
  backgroundMusicId,
  htmlThemeUrl,
  cover,
  pages,
  draftUri,
  createdAt: originalCreatedAt,
}: {
  agent: AtpAgent
  i18n: I18n
  did: string
  title: string
  description: string
  themeId: string
  /**
   * The creator's music choice, in the record's spelling (`'none'` for silence).
   * Written as its own field, never folded into the theme: they are two settings
   * that are changed independently, and one must not imply the other.
   */
  backgroundMusicId?: string
  htmlThemeUrl?: string
  cover: FlashBookCover
  pages: FlashBookPage[]
  /** The record this book already is, when it was loaded from a draft. */
  draftUri?: string | null
  /** The record's own `createdAt`, carried across an in-place update. */
  createdAt?: string | null
}): Promise<SavedFlashBookRef> {
  const {cover: coverOut, pages: pagesOut} = await uploadFlashBookMedia({
    agent,
    i18n,
    did,
    cover,
    pages,
  })

  // The record is replaced wholesale on update, so its creation date has to be
  // carried over by hand or the book would appear to have been rewritten today.
  const createdAt = originalCreatedAt ?? new Date().toISOString()
  const record = {
    $type: 'app.bsky.feed.flashbook',
    title: title.trim() || 'Untitled book',
    description,
    status: 'draft',
    themeId,
    // Absent when nothing was ever chosen, which is how an older book keeps its
    // theme's own bed rather than being silenced by a save.
    backgroundMusicId,
    behaviorSettings: htmlThemeUrl ? {htmlThemeUrl} : undefined,
    cover: coverOut,
    pages: pagesOut,
    pageCount: pagesOut.length,
    exerciseCount: countExercises(pagesOut),
    createdAt,
  }

  const target = flashbookPublishTarget(draftUri)
  const res =
    target.mode === 'update'
      ? await agent.com.atproto.repo.putRecord({
          repo: did,
          collection: 'app.bsky.feed.flashbook',
          rkey: target.rkey,
          record,
        })
      : await agent.com.atproto.repo.createRecord({
          repo: did,
          collection: 'app.bsky.feed.flashbook',
          record,
        })

  return {uri: res.data.uri, cid: res.data.cid, createdAt, cover: coverOut}
}
